// ─────────────────────────────────────────────────────────────────
// Raw HTML Input Component
// ─────────────────────────────────────────────────────────────────

import { useState, useRef, useEffect } from 'react';

interface RawHTMLInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  helper?: string;
  placeholder?: string;
  rows?: number;
  minHeight?: string;
  maxHeight?: string;
  language?: 'html' | 'xml' | 'svg';
  readOnly?: boolean;
  showPreview?: boolean;
  previewLabel?: string;
  sanitize?: boolean;
}

export function RawHTMLInput({
  label,
  value,
  onChange,
  helper,
  placeholder = '<!-- Paste your HTML here -->',
  rows = 6,
  minHeight = '120px',
  maxHeight = '400px',
  readOnly = false,
  showPreview = true,
  previewLabel = 'Preview',
  sanitize = true,
}: RawHTMLInputProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [localValue, setLocalValue] = useState(value);

  // Sync external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [localValue]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Basic validation
    if (sanitize && newValue) {
      try {
        // Simple HTML validation - check for unclosed tags
        const temp = document.createElement('div');
        temp.innerHTML = newValue;
        // If innerHTML is empty but we had content, it might be invalid
        if (newValue.trim() && !temp.innerHTML.trim()) {
          setError('Invalid HTML structure');
        } else {
          setError(null);
        }
      } catch {
        setError('Invalid HTML');
      }
    }

    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Tab key support - insert spaces instead of moving focus
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.currentTarget.selectionStart;
      const end = e.currentTarget.selectionEnd;
      const newValue = localValue.substring(0, start) + '  ' + localValue.substring(end);
      setLocalValue(newValue);
      onChange(newValue);

      // Move cursor after inserted spaces
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = start + 2;
          textareaRef.current.selectionEnd = start + 2;
        }
      }, 0);
    }
  };

  // Format HTML
  const formatHTML = () => {
    try {
      const formatted = localValue
        .replace(/>\s+</g, '>\n<') // Add line breaks between tags
        .split('\n')
        .filter((line) => line.trim())
        .map((line) => line.trim())
        .join('\n');

      setLocalValue(formatted);
      onChange(formatted);
    } catch {
      // If formatting fails, keep as is
    }
  };

  // Sanitize HTML (remove potentially dangerous content)
  const sanitizeHTML = (html: string): string => {
    if (!sanitize) return html;

    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Remove script tags and event handlers
    const scripts = temp.querySelectorAll(
      'script, iframe[src*="javascript:"], *[onclick], *[onerror], *[onload]',
    );
    scripts.forEach((el) => {
      if (el.tagName === 'SCRIPT') {
        el.remove();
      } else {
        // Remove event attributes
        Array.from(el.attributes).forEach((attr) => {
          if (attr.name.startsWith('on')) {
            el.removeAttribute(attr.name);
          }
        });
      }
    });

    return temp.innerHTML;
  };

  // Get sanitized preview
  const getPreviewHTML = () => {
    const sanitized = sanitizeHTML(localValue);
    return sanitized || '<div class="text-muted text-sm">Empty content</div>';
  };

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between">
          <label className="field-label">{label}</label>
          <div className="flex gap-2">
            {/* Format button */}
            <button
              type="button"
              onClick={formatHTML}
              className="text-xs text-muted hover:text-ink-soft"
              title="Format HTML"
            >
              <svg className="inline h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            {/* Preview toggle */}
            {showPreview && (
              <button
                type="button"
                onClick={() => setIsPreviewOpen(!isPreviewOpen)}
                className="text-xs text-muted hover:text-ink-soft"
              >
                {isPreviewOpen ? 'Hide Preview' : 'Show Preview'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Textarea */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={rows}
          readOnly={readOnly}
          className={`field-input resize-none font-mono text-sm ${error ? 'border-danger/50' : ''}`}
          style={{
            minHeight,
            maxHeight,
            overflow: 'auto',
            fontFamily: 'Monaco, Menlo, "Courier New", monospace',
            fontSize: '13px',
            lineHeight: '1.6',
          }}
          spellCheck={false}
        />

        {/* Line numbers (optional) */}
        <div
          className="pointer-events-none absolute top-0 bottom-0 left-0 flex w-8 flex-col items-center text-xs text-muted opacity-50 select-none"
          style={{ paddingTop: '10px' }}
        >
          {localValue.split('\n').map((_, i) => (
            <span key={i}>{i + 1}</span>
          ))}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p className="flex items-center gap-1 text-xs text-danger">
          <span>⚠️</span> {error}
        </p>
      )}

      {/* Helper text */}
      {helper && !error && <p className="text-xs text-muted">{helper}</p>}

      {/* Character/line count */}
      <div className="flex justify-between text-xs text-muted">
        <span>{localValue.split('\n').length} lines</span>
        <span>{localValue.length} characters</span>
      </div>

      {/* Preview */}
      {isPreviewOpen && showPreview && (
        <div className="mt-3 overflow-hidden rounded-lg border border-line">
          <div className="border-b border-line bg-bg-soft px-3 py-1.5 text-xs font-medium text-ink-soft">
            {previewLabel}
          </div>
          <div
            className="max-h-[300px] overflow-auto p-3"
            dangerouslySetInnerHTML={{ __html: getPreviewHTML() }}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Code Editor with Syntax Highlighting (Lightweight)
// ─────────────────────────────────────────────────────────────────

interface CodeEditorProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  language?: 'html' | 'css' | 'javascript' | 'xml';
  helper?: string;
  rows?: number;
  readOnly?: boolean;
}

export function CodeEditor({
  label,
  value,
  onChange,
  language = 'html',
  helper,
  rows = 8,
  readOnly = false,
}: CodeEditorProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Simple syntax highlighting via regex (lightweight)
  // const highlightHTML = (code: string) => {
  //   // This is a very basic highlighter - for production use a proper library
  //   const highlighted = code
  //     // Tags
  //     .replace(/(&lt;\/?)(\w+)([^&gt;]*)(&gt;)/g, (match, open, tag, attrs, close) => {
  //       return `${open}<span class="text-purple-600">${tag}</span>${attrs}${close}`;
  //     })
  //     // Attributes
  //     .replace(/\s(\w+)=/g, ' <span class="text-accent">$1</span>=')
  //     // Strings
  //     .replace(/="([^"]*)"/g, '="<span class="text-ok">$1</span>"')
  //     // Comments
  //     .replace(/(&lt;!--.*?--&gt;)/g, '<span class="text-muted">$1</span>');

  //   return highlighted;
  // };

  const fullscreenClass = isFullscreen
    ? 'fixed inset-0 z-50 overflow-y-auto bg-bg px-6 py-8'
    : '';

  return (
    <div className={`${fullscreenClass}`}>
      {label && (
        <div className="mb-2 flex items-center justify-between">
          <label className="field-label">{label}</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="text-xs text-muted hover:text-ink-soft"
            >
              {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            </button>
          </div>
        </div>
      )}

      <div className="relative overflow-hidden rounded-xl border border-line-strong bg-bg-soft">
        {/* Editor toolbar */}
        <div className="flex items-center gap-2 border-b border-line bg-card px-3 py-2">
          <span className="chip px-2 py-0.5 font-mono text-[10px] normal-case tracking-wide">
            {language.toUpperCase()}
          </span>
          <div className="flex-1" />
          <span className="text-[11px] text-muted">{value.split('\n').length} lines</span>
        </div>

        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          readOnly={readOnly}
          className="w-full resize-none bg-transparent font-mono text-[13px] leading-[1.6] text-ink caret-accent focus:outline-none"
          style={{
            padding: '14px 16px',
            fontFamily: 'Monaco, Menlo, "Courier New", monospace',
            lineHeight: '1.6',
            minHeight: '200px',
          }}
          spellCheck={false}
        />
      </div>

      {helper && <p className="mt-1 text-xs text-muted">{helper}</p>}
    </div>
  );
}
